import * as React from 'react';
import { core } from '@tock/coretypes';
import { debounce, map } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import { getRequiredMarkerIfNeeded } from '../../../utils/inputUtils';
import { requireFields } from '../../../utils/FormValidators';
import BaseComponent from '../../BaseComponent';
import Field from '../Field';
import Fields from '../Fields';
import Select from '../../inputs/Select';
import TextInput from '../../inputs/DeprecatedTextInput';
import connect, { DefaultConnectProps } from '../../../utils/connect';
import phoneCountryCodes, { PhoneCountryCodeEntry, SANCTIONED_PHONE_COUNTRY_CODES_SET } from '../../../constants/phoneCountryCodes';

interface Props {
  phone: ReduxFormField<string>;
  phoneCountryCode: ReduxFormField<string>;
  'data-testid'?: string;
  disabled?: boolean;
  phonePlaceholder?: string;
  requiredFields?: string[];
  label?: string;
  hint?: string;
  labelOptional?: boolean;
  disallowSanctionedCodes?: boolean;
  onValidatePhone?(isPhoneValid: boolean, smsPhoneConsentStatus: core.SmsPhone.StatusType): void;

  onValidatePhoneRequest(
    request: core.ValidatePhoneRequest
  ): Promise<ProtoResponse<core.ValidatePhoneResponse, {}>>;
}

interface State {
  asyncPhoneValidationMessage?: string;
  isValid: boolean;
}

interface PhoneFormData {
  phone: string;
  phoneCountryCode: string;
}

const makePhoneCountryCodeSelections = (disallowSanctionedCodes: boolean): SelectOption[] => map(
  phoneCountryCodes,
  (entry: PhoneCountryCodeEntry) => ({
    key: `${entry.phone} ${entry.iso}`,
    value: `${entry.phone} ${entry.iso}`,
    label: `${entry.name} ${entry.phone}`,
    display: `${entry.name} ${entry.phone}`,
    disabled: disallowSanctionedCodes && SANCTIONED_PHONE_COUNTRY_CODES_SET.has(entry.phone),
  })
);

/**
 * Convert numeric SmsPhone.StatusType enum values to their string representations.
 * The backend API returns numeric enum values, but our utilities expect string values.
 *
 * Copied from consumer/src/client/js/utils/smsUtils.ts
 */
const STATUS_TYPE_MAP: Record<number, core.SmsPhone.StatusType> = {
  0: 'NOT_SPECIFIED',
  1: 'OPT_OUT_HARD',
  2: 'OPT_IN',
  3: 'OPT_OUT_SOFT',
};

function normalizeSmsPhoneStatusType(
  status: core.SmsPhone.StatusType | number | null | undefined
): core.SmsPhone.StatusType {
  if (status === null || status === undefined) {
    return 'NOT_SPECIFIED';
  }
  // If it's already a string, return as-is
  if (typeof status === 'string') {
    return status;
  }
  // If it's a number, convert using the map
  return STATUS_TYPE_MAP[status] ?? 'NOT_SPECIFIED';
}

export function validatePhoneFieldset(
  data: ReduxFormValues<PhoneFormData>,
  validatePhoneNumber: (
    request: core.ValidatePhoneRequest
  ) => Promise<ProtoResponse<core.ValidatePhoneResponse>>,
  blockSubmitOnInvalidNumber = false
): ReduxFormFieldErrors<PhoneFormData> {
  const errors = requireFields(data, 'phone', 'phoneCountryCode');

  if (blockSubmitOnInvalidNumber) {
    validatePhoneNumber(data).then((response) => {
      if (!response.payload.isValid) {
        errors.phone = 'This is not a valid phone number';
      }
    });
  }

  return errors;
}

class PhoneFieldset extends BaseComponent<Props & DefaultConnectProps, State> {
  constructor(props: Props & DefaultConnectProps) {
    super(props);

    this.state = {
      asyncPhoneValidationMessage: undefined,
      isValid: false,
    };
  }
  componentDidMount() {
    this.validatePhoneRequest(this.props.phone.value, this.props.phoneCountryCode.value);
  }

  debouncePhone = debounce(
    (phone: string, phoneCountryCode: string) => {
      this.validatePhoneRequest(phone, phoneCountryCode);
    },
    800,
    { trailing: true }
  );

  validatePhone(eventOrValue: React.ChangeEvent<HTMLInputElement>): void {
    const value = oc(eventOrValue).target.value('');
    this.debouncePhone(value, this.props.phoneCountryCode.value);
  }

  validatePhoneCountryCode(eventOrValue: React.ChangeEvent<HTMLInputElement>): void {
    const value = oc(eventOrValue).target.value('');
    this.debouncePhone(this.props.phone.value, value);
  }

  validatePhoneRequest(phone: string | undefined, phoneCountryCode: string | undefined): void {
    const { onValidatePhoneRequest, onValidatePhone } = this.props;
    if (!phone) {
      return;
    } else if (!phoneCountryCode) {
      this.setState({
        asyncPhoneValidationMessage: 'Missing phone country code',
        isValid: false,
      });
      return;
    } else {
      onValidatePhoneRequest({ phone, phoneCountryCode }).then((response) => {
        const isValid = response.payload.isValid;

        const strippedCountryCode = phoneCountryCode.split(' ')[0];
        this.setState({
          asyncPhoneValidationMessage: isValid
            ? `${strippedCountryCode} ${response.payload.beautifiedPhone} is a valid number`
            : 'Hmm, we could not validate this phone number',
          isValid,
        });
        if (onValidatePhone) {
          onValidatePhone(
            isValid,
            normalizeSmsPhoneStatusType(response.payload.smsPhoneConsentStatus)
          );
        }
        return;
      });
    }
  }

  render() {
    const {
      disabled,
      disallowSanctionedCodes = false,
      phone,
      phoneCountryCode,
      requiredFields,
      label,
      hint,
      labelOptional,
      phonePlaceholder,
    } = this.props;

    const areFieldsRequired = requiredFields || [];

    const { asyncPhoneValidationMessage } = this.state;
    return (
      <Fields>
        <Field label="Country code" labelFor="phoneCountryCode">
          <Select
            id="phoneCountryCode"
            field={phoneCountryCode}
            onChange={this.validatePhoneCountryCode}
            options={makePhoneCountryCodeSelections(disallowSanctionedCodes)}
            placeholder="Country code"
            data-testid="country-code"
          />
        </Field>

        <Field
          label={label ? label : 'Phone number'}
          field={phone}
          labelFor="phone"
          key="PhoneField"
          warn={this.state.isValid ? undefined : asyncPhoneValidationMessage}
          hint={hint ? hint : undefined}
          labelOptional={labelOptional}
          valid={this.state.isValid ? asyncPhoneValidationMessage : undefined}
        >
          <TextInput
            field={phone}
            id="phone"
            autoComplete="tel"
            placeholder={
              phonePlaceholder
                ? phonePlaceholder
                : `Phone number ${getRequiredMarkerIfNeeded(areFieldsRequired, phone)}`
            }
            disabled={disabled}
            onChange={this.validatePhone}
            type="tel"
            data-testid={this.props['data-testid']}
          />
        </Field>
      </Fields>
    );
  }
}
export default connect<Props, DefaultConnectProps>(PhoneFieldset);
